import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  EfCompartmentTopology,
  EfMetaboliteParameters,
  EfModelParameterRow,
  EfModelParametersSet,
  EfParametersTopology,
} from 'src/app/shared/models/ef-model-parameters.model';
import { EFateConstants } from 'src/app/shared/utils/efate-constants';
import { EfDataModelParametersLogicService } from './ef-data-model-parameters.logic.service';

@Component({
  selector: 'app-ef-data-model-parameters',
  templateUrl: './ef-data-model-parameters.component.html',
  styleUrls: ['./ef-data-model-parameters.component.css']
})
export class EFDataModelParametersComponent implements OnInit {
  @Input({ required: true }) modelType!: string;
  @Input({ required: true }) topology!: EfParametersTopology;
  @Input() nodeNames: Record<string, string> = {};
  @Input() savedSet?: EfModelParametersSet;

  @Output() saved = new EventEmitter<EfModelParametersSet>();
  @Output() closed = new EventEmitter<void>();

  readonly texts = EFateConstants.MODEL_PARAMETERS;
  readonly metaboliteModelOptions = EFateConstants.METABOLITE_MODEL_OPTIONS;
  readonly selectableMetaboliteKeys = EFateConstants.PRIMARY_METABOLITE_KEYS;

  parameters: EfModelParametersSet = { parentRows: [], metabolites: [] };

  constructor(private readonly logicService: EfDataModelParametersLogicService) { }

  get header(): string {
    return `${this.texts.DIALOG_TITLE} - ${this.modelType}`;
  }

  ngOnInit(): void {
    this.parameters = this.buildParameters(this.savedSet);
  }

  trackByKey(_index: number, row: EfModelParameterRow): string {
    return row.key;
  }

  trackByNodeKey(_index: number, metabolite: EfMetaboliteParameters): string {
    return metabolite.nodeKey;
  }

  getNodeName(nodeKey: string): string {
    return this.nodeNames[nodeKey] || nodeKey;
  }

  isPrimaryMetabolite(nodeKey: string): boolean {
    return this.findCompartment(nodeKey)?.isPrimary ?? false;
  }

  getMetaboliteModelType(nodeKey: string): string {
    return this.parameters.metabolites.find((metabolite: EfMetaboliteParameters) => metabolite.nodeKey === nodeKey)?.modelType
      ?? EFateConstants.DEFAULT_METABOLITE_MODEL;
  }

  onInitialValueChange(row: EfModelParameterRow, value: number | null): void {
    this.logicService.setInitialValue(row, value);
  }

  onFixedChange(row: EfModelParameterRow, value: boolean): void {
    row.isFixed = value;
  }

  onLowerLimitChange(row: EfModelParameterRow, value: number | null): void {
    this.logicService.setLowerLimit(row, value);
  }

  onUpperLimitChange(row: EfModelParameterRow, value: number | null): void {
    this.logicService.setUpperLimit(row, value);
  }

  onMetaboliteModelChange(nodeKey: string, modelType: string): void {
    const index = this.parameters.metabolites.findIndex((metabolite: EfMetaboliteParameters) => metabolite.nodeKey === nodeKey);
    const current = this.parameters.metabolites[index];
    const compartment = this.findCompartment(nodeKey);

    if (!current || !compartment || !compartment.isPrimary || current.modelType === modelType) {
      return;
    }

    this.parameters.metabolites[index] = this.logicService.createMetabolite(compartment, modelType, this.nodeNames);
  }

  onReset(): void {
    this.parameters = this.buildParameters();
  }

  onClose(): void {
    this.logicService.fillMissingInitialValues(this.parameters, this.modelType, this.topology, this.nodeNames);
    this.saved.emit(this.logicService.cloneSet(this.parameters));
    this.closed.emit();
  }

  private findCompartment(nodeKey?: string): EfCompartmentTopology | undefined {
    return this.topology.metabolites.find((compartment: EfCompartmentTopology) => compartment.nodeKey === nodeKey);
  }

  private buildParameters(saved?: EfModelParametersSet): EfModelParametersSet {
    return this.logicService.createParametersSet(this.modelType, this.topology, this.nodeNames, saved);
  }
}
